(() => {
  const root = document.documentElement;
  const initializedAttribute = 'data-blog-tag-counts-initialized';
  const countSelector = '[data-blog-tag-count-url]';
  const responseSelector = '[data-blog-tag-count-result]';
  const sectionId = 'abi-sections__blog_tag_count';
  const cachePrefix = 'handmade-park:blog-tag-count:';
  const cacheLifetimeMs = 30 * 60 * 1000;
  const maxConcurrentRequests = 4;

  if (root.hasAttribute(initializedAttribute)) return;
  root.setAttribute(initializedAttribute, '');

  const readCache = (tagUrl) => {
    try {
      const cached = JSON.parse(localStorage.getItem(cachePrefix + tagUrl));
      if (!cached || Date.now() - cached.savedAt > cacheLifetimeMs) return null;
      return Number.isInteger(cached.count) && cached.count >= 0 ? cached.count : null;
    } catch (error) {
      return null;
    }
  };

  const writeCache = (tagUrl, count) => {
    try {
      localStorage.setItem(cachePrefix + tagUrl, JSON.stringify({ count, savedAt: Date.now() }));
    } catch (error) {
      // ストレージを利用できない環境でも、件数表示そのものは継続する。
    }
  };

  const showCount = (elements, count) => {
    elements.forEach((element) => {
      element.textContent = ` (${count})`;
      element.setAttribute('aria-label', `記事件数 ${count}件`);
      element.removeAttribute('aria-busy');
    });
  };

  const hideUnavailableCount = (elements) => {
    elements.forEach((element) => element.remove());
  };

  const countRequestUrl = (tagUrl) => {
    const url = new URL(tagUrl, window.location.origin);
    url.searchParams.set('section_id', sectionId);

    // テーマプレビュー中も同じ未公開テーマからSectionを取得する。
    const currentUrl = new URL(window.location.href);
    ['preview_theme_id', '_fd', 'pb'].forEach((name) => {
      const value = currentUrl.searchParams.get(name);
      if (value) url.searchParams.set(name, value);
    });
    return url.toString();
  };

  const fetchCount = async (tagUrl) => {
    const response = await fetch(countRequestUrl(tagUrl), {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });
    if (!response.ok) throw new Error(`Tag count request failed: ${response.status}`);

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const result = doc.querySelector(responseSelector);
    const count = Number.parseInt(result?.dataset.blogTagCountValue, 10);
    if (!Number.isInteger(count) || count < 0) throw new Error('Tag count response was invalid.');
    return count;
  };

  const runQueue = async (jobs) => {
    let nextJobIndex = 0;
    const worker = async () => {
      while (nextJobIndex < jobs.length) {
        const job = jobs[nextJobIndex];
        nextJobIndex += 1;
        try {
          const count = await fetchCount(job.tagUrl);
          writeCache(job.tagUrl, count);
          showCount(job.elements, count);
        } catch (error) {
          // 誤った0件を表示するより、取得できない場合は件数表示だけを隠す。
          hideUnavailableCount(job.elements);
        }
      }
    };

    const workerCount = Math.min(maxConcurrentRequests, jobs.length);
    await Promise.all(Array.from({ length: workerCount }, () => worker()));
  };

  const initialize = () => {
    const groupedElements = new Map();
    document.querySelectorAll(countSelector).forEach((element) => {
      if (element.dataset.blogTagCountState === 'ready') return;
      element.dataset.blogTagCountState = 'ready';
      element.setAttribute('aria-busy', 'true');

      const tagUrl = element.dataset.blogTagCountUrl;
      if (!tagUrl) {
        element.remove();
        return;
      }

      if (!groupedElements.has(tagUrl)) groupedElements.set(tagUrl, []);
      groupedElements.get(tagUrl).push(element);
    });

    const jobs = [];
    groupedElements.forEach((elements, tagUrl) => {
      const cachedCount = readCache(tagUrl);
      if (cachedCount !== null) {
        showCount(elements, cachedCount);
      } else {
        jobs.push({ tagUrl, elements });
      }
    });

    if (jobs.length) runQueue(jobs);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
  document.addEventListener('shopify:section:load', initialize);
})();
